import { type ReactElement } from 'react'
import { Plus, Wrench } from 'lucide-react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { getOrgSettings } from '@/features/orgs/api/orgsService'
import { IndirectCostSubcategorySelect } from '@/features/indirect-costs/components/IndirectCostSubcategorySelect'
import SubcategoriesManagerPopover from '@/features/indirect-costs/components/SubcategoriesManagerPopover'
import { useIndirectCostSubcategories } from '@/features/indirect-costs/hooks/useIndirectCostSubcategories'
import { createIndirectCost } from '@/features/indirect-costs/api/indirectCostsService'
import { createIndirectCostSubcategory } from '@/features/indirect-costs/api/indirectCostSubcategoriesService'

interface AddCostCardProps {
  year: number
  monthIndex: number
}

export function AddCostCard({ year, monthIndex }: AddCostCardProps): ReactElement {
  const { t } = useTranslation('features-indirect-costs')
  const queryClient = useQueryClient()
  const { activeOrgId } = useActiveOrg()

  const { data: orgSettings } = useQuery({
    queryKey: ['org-settings', activeOrgId],
    queryFn: () => (activeOrgId ? getOrgSettings(activeOrgId) : Promise.reject('No org')),
    enabled: !!activeOrgId,
  })

  const {
    data: subcategories = [],
    isLoading: subcatsLoading,
    isError: subcatsError,
  } = useIndirectCostSubcategories()

  const handleSelect = async (subcategoryUuid: string): Promise<void> => {
    if (!orgSettings) return
    const startDate = new Date(Date.UTC(year, monthIndex, 1))
    const endDate = new Date(Date.UTC(year, monthIndex + 1, 1))
    try {
      await createIndirectCost({
        subcategoryUuid,
        originalAmount: 0,
        originalCurrency: orgSettings.reportingCurrency,
        effectiveFrom: format(startDate, 'yyyy-MM-dd'),
        effectiveTo: format(endDate, 'yyyy-MM-dd'),
        description: '',
      })
      await queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : t('toasts.createFailed'))
    }
  }

  const handleCreateSubcategory = async (name: string): Promise<string> => {
    const created = await createIndirectCostSubcategory({
      displayName: name,
      category: 'OPEX',
    })
    await queryClient.invalidateQueries({ queryKey: ['indirect-costs', 'subcategories'] })
    return created.uuid
  }

  return (
    <div className="flex items-center gap-2">
      <IndirectCostSubcategorySelect
        className="flex-1 min-w-0"
        value={null}
        onChange={uuid => void handleSelect(uuid)}
        subcategories={subcategories.filter(s => !s.system)}
        isLoading={subcatsLoading}
        isError={subcatsError}
        placeholder={t('subcategorySelect.placeholder', { defaultValue: 'Pick subcategory...' })}
        emptyLabel={t('subcategorySelect.empty', { defaultValue: 'No subcategories' })}
        loadingLabel={t('subcategorySelect.loading', { defaultValue: 'Loading...' })}
        onCreateSubcategory={handleCreateSubcategory}
        createLabel={t('subcategories.actions.add', { defaultValue: 'Add subcategory' })}
        trigger={
          <button
            type="button"
            className="w-full rounded-lg border border-dashed border-border bg-card p-3 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('actions.add', { defaultValue: 'Add a cost' })}
          </button>
        }
      />
      <SubcategoriesManagerPopover
        onMutated={() =>
          void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
        }
        trigger={
          <button
            type="button"
            className="shrink-0 self-stretch rounded-lg border border-dashed border-border bg-card px-3 text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
            aria-label={t('subcategories.button')}
          >
            <Wrench className="h-4 w-4" />
          </button>
        }
      />
    </div>
  )
}
